import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "../motion/Reveal";
import { IMG } from "../../config/site";
import { useUI } from "../../context/UIContext";

const TILES = [
  { title: "Guest Rooms", biz: "Luxury hotel", enq: "Hotel supply partnership", text: "A considered welcome for every stay.", img: IMG.room, alt: "A warmly lit luxury hotel guest room" },
  { title: "Restaurants", biz: "Restaurant / fine dining", enq: "Commercial enquiry", text: "Complementing exceptional cuisine.", img: IMG.restaurant, alt: "An elegant restaurant dining room with warm lighting" },
  { title: "Banquets & Events", biz: "Banquet / events", enq: "Commercial enquiry", text: "A refined detail for memorable occasions.", img: IMG.banquet, alt: "A long banquet table dressed with flowers and glassware" },
  { title: "Spas & Wellness", biz: "Spa / wellness", enq: "Commercial enquiry", text: "Thoughtful presentation for considered experiences.", img: IMG.spa, alt: "Rolled white spa towels beside a glass carafe and fresh greenery" },
  { title: "Hospitals & Healthcare", biz: "Hospital / healthcare", enq: "Commercial enquiry", text: "A considered hydration choice for patients and visitors.", img: IMG.hospital, alt: "A calm, well-appointed private hospital room" },
  { title: "Offices & Boardrooms", biz: "Corporate / government office", enq: "Commercial enquiry", text: "A polished detail for government and corporate meetings.", img: IMG.office, alt: "An elegant boardroom table set for a meeting" },
  { title: "Lounges & Clubs", biz: "Lounge / club", enq: "Commercial enquiry", text: "Quiet luxury for members and premium travellers.", img: IMG.lounge, alt: "A contemporary lounge with warm wood and soft seating" },
];

const Tile = ({ t, i, onEnquire }) => (
  <motion.article className="group w-[78vw] max-w-[340px] shrink-0 snap-start sm:w-[44%] sm:max-w-none lg:w-[calc((100%-60px)/3.4)]" data-testid={`usecase-tile-${i}`}
    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 1, delay: Math.min(i, 4) * 0.1, ease: [0.22, 1, 0.36, 1] }}>
    <button type="button" onClick={() => onEnquire(t)} data-testid={`usecase-enquire-${i}`} aria-label={`Enquire about TOYAM for ${t.title}`} className="block w-full text-center">
    <div className="aspect-[4/3] overflow-hidden lg:aspect-[5/4]">
      <img src={t.img} alt={t.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.6s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.07]" />
    </div>
    <div className="pt-5 text-center">
      <h3 className="font-sans text-[11px] font-bold uppercase tracking-[0.26em] text-[#26231F]">{t.title}</h3>
      <p className="font-serif-lux mt-2 text-lg italic text-[#746D64]">{t.text}</p>
      <span className="mt-3 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-[#8C6A3C] transition-transform duration-500 group-hover:translate-x-1">Enquire<ArrowRight className="h-3 w-3" strokeWidth={1.6} aria-hidden="true" /></span>
    </div>
    </button>
  </motion.article>
);

const Arrow = ({ dir, onClick }) => (
  <button type="button" onClick={onClick} aria-label={dir < 0 ? "Previous moments" : "Next moments"} data-testid={`usecases-${dir < 0 ? "prev" : "next"}-btn`}
    className="grid h-12 w-12 place-items-center rounded-full border border-[#B18A4A]/50 text-[#26231F] transition-colors duration-500 hover:bg-[#26231F] hover:text-[#F8F3E9]">
    {dir < 0 ? <ArrowLeft className="h-4 w-4" strokeWidth={1.4} /> : <ArrowRight className="h-4 w-4" strokeWidth={1.4} />}
  </button>
);

export const UseCases = () => {
  const track = useRef(null);
  const { openEnquiry } = useUI();
  const onEnquire = (t) => openEnquiry(t.enq, t.biz);
  const scroll = (dir) => {
    const el = track.current;
    const tile = el?.querySelector("article");
    if (el && tile) el.scrollBy({ left: dir * (tile.offsetWidth + 20), behavior: "smooth" });
  };
  return (
    <section data-testid="usecases-section" className="bg-[#F8F3E9] py-20 sm:py-28">
      <div className="mx-auto grid max-w-[1440px] gap-10 lg:grid-cols-[0.7fr_4fr] lg:items-center lg:gap-12 lg:pl-12">
        <Reveal className="flex items-end justify-between px-5 sm:px-8 lg:block lg:px-0">
          <div>
            <h2 className="font-sans text-sm font-bold uppercase leading-[2.1] tracking-[0.42em] text-[#26231F]">
              Elevating<br />Hospitality<br />Moments
            </h2>
            <span className="mt-6 block h-px w-12 bg-[#B18A4A]" aria-hidden="true" />
          </div>
          <div className="flex gap-3 lg:mt-10">
            <Arrow dir={-1} onClick={() => scroll(-1)} />
            <Arrow dir={1} onClick={() => scroll(1)} />
          </div>
        </Reveal>
        <div ref={track} className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-px-5 px-5 pb-2 sm:scroll-px-8 sm:px-8 lg:scroll-px-0 lg:px-0 lg:pr-12" data-testid="usecases-track">
          {TILES.map((t, i) => <Tile key={t.title} t={t} i={i} onEnquire={onEnquire} />)}
        </div>
      </div>
    </section>
  );
};
